import { useState, useEffect } from 'react';
import getContent from 'lib/getContent';

/**
 * useWeekContentData – Fetches week content by slug.
 *
 * Keeps data fetching separate from context state management.
 * This hook handles the API call and loading states, while WeekProvider
 * manages the week state once loaded.
 *
 * @param {string} weekSlug - Slug of the week to fetch
 * @param {string} productSlug - Product slug (e.g., 'wlc')
 * @param {string} authToken - Authentication token from Redux
 * @param {Object} options - Additional options
 * @param {number} options.depth - How many levels of children to include
 * @param {boolean} options.enabled - Whether to fetch immediately (default: true)
 *
 * @returns {Object} { week, isLoading, error, refetch }
 *
 * @example
 * function WeekPage() {
 *   const { authToken } = useSelector(state => state.user);
 *   const { week, isLoading, error } = useWeekContentData(
 *     'wlc-week-1-healthy-eating',
 *     'wlc',
 *     authToken
 *   );
 *
 *   if (isLoading) return <Loading />;
 *   if (error) return <Error message={error} />;
 *
 *   return (
 *     <WeekProvider initialWeek={week}>
 *       <WeekContent />
 *     </WeekProvider>
 *   );
 * }
 */
export const useWeekContentData = (weekSlug, productSlug, authToken, options = {}) => {
  const { depth = null, enabled = true } = options;
  const query = depth != null ? `?depth=${depth}` : '';
  const [week, setWeek] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchWeek = async () => {
    if (!weekSlug || !authToken || !enabled) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await getContent(weekSlug, productSlug, authToken, query);
      setWeek(data);
    } catch (err) {
      console.error('Error fetching week:', err);
      setError(err.message || 'Failed to load week');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWeek();
  }, [weekSlug, productSlug, authToken, query, enabled]);

  return {
    week,
    isLoading,
    error,
    refetch: fetchWeek
  };
};

export default useWeekContentData;
